﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The logout page. Clears the current API token (from browser sessions storage) and then redirects
// to clear the session from the server.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { TokenRequestStatus, useAuthToken } from "~base/Services/API/AuthToken";
import { SplashPage } from "~base/Pages/Shared/SplashPage";
import { useServerContext } from "~base/Services/ServerContext";
import { triggerLogoutBroadcast } from "~base/Services/LogoutBroadcastListener";
import { useSearchParams } from "react-router-dom";
import { LogoutPageCustomizations } from "./Customization";
import { useGetUser } from "~base/Services/API/Account/GetUser";
import { User } from "~base/Services/API/Account/Model";
import { SettingsContext, useSettings } from "~base/Settings/SettingsContext";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type LogoutProps = {
    customizations?: LogoutPageCustomizations;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const LogoutPage: React.FunctionComponent<LogoutProps> = ({
    customizations,
}) => {
    const [redirected, setRedirected] = React.useState(false);
    const [user, setUser] = React.useState<User>(null);
    const [loadUserFailed, setLoadUserFailed] = React.useState(false);

    const [_, token] = useAuthToken({});
    const [searchParams] = useSearchParams();
    const serverContext = useServerContext();
    const settings = useSettings();

    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const formRef = React.useRef<HTMLFormElement>(undefined);

    const tenantFromUser =
        serverContext.user?.Tenant?.Name ?? user?.Tenant?.Name;

    const logoutPathEnd =
        window.location.pathname.toLowerCase().indexOf("/account/logout") +
        "/account/logout/".length;
    const tenantFromPath =
        window.location.pathname.length > logoutPathEnd
            ? window.location.pathname.substring(logoutPathEnd)
            : null;

    const paramBroadcast = searchParams.get("broadcast");
    const tenantFromQuery = searchParams.get("tenantName");

    const tenantName = tenantFromUser ?? tenantFromPath ?? tenantFromQuery;
    const tenantNameContainsEncodedData =
        tenantName != null && tenantName.indexOf("%") > -1;

    const callGetUser =
        customizations?.requiresUser === true &&
        loadUserFailed == false &&
        token?.access_token != null &&
        new Date(token.absolute_expiration).getTime() > new Date().getTime() &&
        serverContext.user == null &&
        user == null;

    const [__, loadUserApi] = useGetUser({
        fromToken: true,
        throwError: false,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                setUser(responseData);
            } else {
                setLoadUserFailed(true);
            }
        },
    });

    const loadUserRef = React.useRef(loadUserApi);

    React.useEffect(() => {
        if (callGetUser) {
            loadUserRef.current();
        }
    }, [callGetUser, loadUserRef]);

    const state =
        customizations?.getState &&
        customizations?.getState(serverContext.user ?? user);
    const search = window.location.search ? `${window.location.search}` : "";
    const method = state ? "post" : "get";

    const logoutFromOtherTab = paramBroadcast == "1";

    if (!logoutFromOtherTab) {
        triggerLogoutBroadcast();
    }

    const redirectDelay = logoutFromOtherTab ? 5000 : 0;

    useAuthToken({
        clearToken: !callGetUser,
        callback: (status: TokenRequestStatus) => {
            setTimeout(() => {
                document.cookie =
                    "CON.access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

                if (status == "Cleared" && redirected == false) {
                    setRedirected(true);

                    // Slight delay to let React finish render updates (otherwise we will cause errors by navigating this way)
                    setTimeout(() => {
                        formRef.current.submit();
                    }, 500);
                }
            }, redirectDelay);
        },
    });

    return (
        <>
            <SplashPage showSplashScreen={false} isLoading={true}></SplashPage>
            <form
                method={method}
                ref={formRef}
                action={`${serverVirtualPath}Account/Logout${search}`}
                id="logoutForm"
            >
                <input type="hidden" name="srv" value="1" />
                <input type="hidden" name="state" value={state} />
                {!tenantNameContainsEncodedData && (
                    <input type="hidden" name="tenantName" value={tenantName} />
                )}
            </form>
        </>
    );
};
